import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/archive';
import { Breadcrumb, PostCardVertical } from '@/components/feature/PostCard';

export default function SearchPage() {
  const { keyword = '' } = useParams();
  const kw = decodeURIComponent(keyword);
  const [q, setQ] = useState(kw);
  const navigate = useNavigate();

  const lower = kw.toLowerCase();
  const matched = articles.filter((a) => {
    const catName = categories.find((c) => c.slug === a.category)?.name ?? '';
    const hay = `${a.title} ${a.summary} ${a.tags.join(' ')} ${catName}`.toLowerCase();
    return hay.includes(lower);
  });

  useEffect(() => {
    setQ(kw);
    document.title = kw ? `搜索：${kw} | 每日吃瓜网` : '站内搜索 | 每日吃瓜网';
  }, [kw]);

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const t = q.trim();
    if (t) navigate(`/search/${encodeURIComponent(t)}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ name: '首页', to: '/' }, { name: kw ? `搜索“${kw}”` : '搜索' }]} />

      <div className="mb-8 max-w-2xl">
        <h1 className="font-heading text-3xl text-foreground-950">
          {kw ? <>“{kw}” 的搜索结果</> : '站内搜索'}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          {kw ? `共找到 ${matched.length} 篇相关瓜报。` : '输入你想吃的瓜、明星或事件关键词进行搜索。'}
        </p>
        <form onSubmit={onSearch} className="mt-5 flex items-center gap-2 rounded-full bg-background-100 px-4 py-3">
          <i className="ri-search-line text-xl text-foreground-400" aria-hidden="true" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="继续搜索：如 塌房 / 恋情 / 红毯…"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            搜索
          </button>
        </form>
      </div>

      {matched.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {matched.map((a) => (
            <PostCardVertical key={a.slug} a={a} />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl bg-background-100 p-10 text-center">
          <i className="ri-search-line text-5xl text-foreground-300" aria-hidden="true" />
          <p className="mt-4 text-lg font-semibold text-foreground-900">暂时没有找到“{kw}”的相关瓜报</p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或从以下栏目开始逛。</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {categories.map((c) => (
              <Link key={c.slug} to={`/column/${c.slug}`} className="rounded-full bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50">
                <i className={`${c.icon} mr-1`} aria-hidden="true" />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}